/**
 * Savings progress (PDP) — live refresh.
 *
 * The savings bar is server-rendered on the product page ONLY when the product is already in the
 * cart (as a one-time purchase). This element listens for `cart:update` (fired on every cart
 * change, bubbling to document) and refetches its own product section, swapping in the fresh bar —
 * so after add-to-cart the bar appears behind the drawer, and it updates/vanishes as the cart
 * quantity or subscription state changes. No page reload required.
 *
 * Self-contained: no imports, listens for the literal event name. Refetching the whole product
 * section is a little heavy but runs only on cart changes and only when the block is present
 * (rendered for qualifying products only — see blocks/savings-progress.liquid).
 */

const inFlight = new WeakSet();

class SavingsProgressPdp extends HTMLElement {
  connectedCallback() {
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:update', this.handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.handleCartUpdate);
  }

  async handleCartUpdate() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || inFlight.has(this)) return;

    inFlight.add(this);
    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
      const response = await fetch(url);
      if (!response.ok) return;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('savings-progress-pdp');
      const next = fresh ? fresh.innerHTML.trim() : '';

      // Only touch the DOM when the markup actually changed, to avoid needless layout churn.
      if (next !== this.innerHTML.trim()) {
        this.innerHTML = next;
      }
    } catch (error) {
      // Silent — the bar just won't live-refresh; a page load will reconcile it.
    } finally {
      inFlight.delete(this);
    }
  }
}

if (!customElements.get('savings-progress-pdp')) {
  customElements.define('savings-progress-pdp', SavingsProgressPdp);
}
